import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { TranslationKeyProvider } from './context/TranslationContext';
import { LanguageProvider } from './context/LanguageContext';
import { NotificationProvider } from './context/NotificationContext';
import { DialogProvider } from './context/DialogContext';
import { useInvites } from './hooks/useInvites';
import Notification from './components/Notification/Notification';
import Login from './views/Login/Login';
import SignUp from './views/SignUp/SignUp';
import UserInfo from './views/SignUp/UserInfo';
import CreateTeam from './views/SignUp/CreateTeam';
import OtpVerification from './views/OtpVerification/OtpVerification';
import Dashboard from './views/ReviewsView/ReviewsView';
import ReviewDetail from './views/ReviewDetail';
import StyleGuide from './views/StyleGuide/StyleGuide';
import './tokens.css';
import './styles/ListStyles.css';
import './App.css';
import { ProfileProvider } from './context/ProfileContext';
import TranslationsPage from './views/TranslationsView/TranslationsView';
import ReviewsPage from './views/ReviewsView/ReviewsView';
import TeamPage from './views/TeamView/TeamView';
import TonePage from './views/ToneView/ToneView';
import TopNavBar from './components/TopNavBar';
import TranslationsView from './views/TranslationsView/TranslationsView';
import ReviewsView from './views/ReviewsView/ReviewsView';
import TeamView from './views/TeamView/TeamView';
import ToneView from './views/ToneView/ToneView';
import LandingPage from './views/LandingPage/LandingPage';
import TermsConditionsPage from './views/TermsConditionsPage/TermsConditionsPage';
import CookiePolicyPage from './views/CookiePolicyPage/CookiePolicyPage';
import AboutPage from './views/AboutPage/AboutPage';
import DocsPage from './views/DocsPage/DocsPage';
import CookieConsent, { Cookies } from "react-cookie-consent";
import ChoosePlan from './views/SignUp/ChoosePlan';

// Loading component
const LoadingScreen = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    backgroundColor: 'var(--color-surfaceprimary)'
  }}>
    <p style={{ 
      font: 'var(--font-title-2)',
      color: 'var(--color-text-primary)'
    }}>Loading...</p>
  </div>
);

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
};

// New Layout for Authenticated Routes
const AppLayout = () => {
  useInvites();

  return (
    <div className="app-container">
      <TopNavBar />
      <div className="main-content">
        <Outlet />
      </div>
    </div>
  );
};

// Cookie name used by CookieConsent component
const COOKIE_CONSENT_NAME = "textaCookieConsent";

// Helper function to check consent status
const hasConsent = (): boolean => {
  return Cookies.get(COOKIE_CONSENT_NAME) === "true";
};

// Function to initialize analytics (Placeholder)
const initializeAnalytics = () => {
  // Replace with your actual analytics initialization code 
  // (e.g., initializing Google Analytics, Mixpanel, etc.)
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return (
        <Routes location={location}>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={user && !localStorage.getItem('isSignUp') ? <Navigate to="/app/overview" replace /> : <Login />} />
      <Route path="/signup" element={user && !localStorage.getItem('isSignUp') ? <Navigate to="/app/overview" replace /> : <SignUp />} />
      <Route path="/verify" element={<OtpVerification />} />
      <Route path="/user-info" element={<UserInfo />} />
      <Route path="/create-team" element={<CreateTeam />} />
      <Route path="/choose-plan" element={<ChoosePlan />} />
      <Route path="/style-guide" element={<StyleGuide />} />
      <Route path="/terms-conditions" element={<TermsConditionsPage />} />
      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/docs/:featureId?" element={<DocsPage />} />
      <Route 
        path="/app" 
        element={
            <ProtectedRoute>
            <AppLayout /> 
            </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<TranslationsView />} />
        <Route path="translations" element={<Navigate to="/app/overview" replace />} />
        <Route path="dashboard" element={<Navigate to="/app/overview" replace />} />
        <Route path="reviews" element={<ReviewsView />} />
        <Route path="review/:reviewId" element={<ReviewDetail />} />
        <Route path="team" element={<TeamView />} />
        <Route path="tone" element={<ToneView />} />
      </Route>
      <Route path="*" element={<Navigate to={user ? "/app/overview" : "/"} replace />} />
        </Routes>
  );
};

const App = () => {
  // Function to handle acceptance 
  const handleAcceptCookie = () => {
    // Initialize analytics only after consent is given
    if (hasConsent()) {
      initializeAnalytics();
    }
  };

  // Function to handle decline (optional)
  const handleDeclineCookie = () => {
    // You might want to remove any non-essential cookies/storage set previously
  };

  // Set global tab title
  useEffect(() => {
    document.title = "Texta";
  }, []);

  // Check consent on initial load
  useEffect(() => {
    if (hasConsent()) {
      initializeAnalytics();
    }
  }, []);

  return (
    <AuthProvider>
      <ProfileProvider>
        <Router>
          <WorkspaceProvider>
            <LanguageProvider>
              <TranslationKeyProvider>
                <NotificationProvider>
                  <DialogProvider>
                    <AppRoutes />
                    <CookieConsent
                      location="bottom"
                      buttonText="Accept"
                      declineButtonText="Decline"
                      cookieName={COOKIE_CONSENT_NAME}
                      style={{
                        background: "var(--surfacesecondary)", 
                        color: "var(--text-secondary)", 
                        borderTop: "1px solid var(--divider)",
                        font: "var(--font-body)",
                        padding: "8px 0",
                        boxShadow: "var(--shadow-flying)",
                        boxSizing: "border-box",
                        width: "100%"
                      }}
                      buttonStyle={{
                        background: "var(--accent)", 
                        color: "var(--onaccent)", 
                        font: "var(--font-body-bold)",
                        borderRadius: "var(--radius-full)",
                        padding: "5px 12px 7px 12px",
                        fontSize: "12px",
                        height: "32px",
                        border: "none",
                        cursor: "pointer",
                      }}
                      declineButtonStyle={{
                        background: "var(--surfacetertiary)", 
                        color: "var(--text-secondary)", 
                        font: "var(--font-body-bold)",
                        borderRadius: "var(--radius-full)",
                        padding: "5px 12px 7px 12px",
                        fontSize: "12px",
                        height: "32px",
                        border: "none",
                        cursor: "pointer",
                        margin: "0 0",
                      }}
                      expires={150}
                      enableDeclineButton
                      onAccept={handleAcceptCookie}
                      onDecline={handleDeclineCookie}
                    >
                      This website uses cookies and local storage to enhance the user experience. See our{" "}
                      <a href="/cookie-policy" style={{ color: "var(--text-primary)", textDecoration: "underline" }}>
                        Cookie Policy
                      </a> for details.
                    </CookieConsent>
                  </DialogProvider>
                </NotificationProvider>
              </TranslationKeyProvider>
            </LanguageProvider>
          </WorkspaceProvider>
        </Router>
      </ProfileProvider>
    </AuthProvider>
  );
};

export default App;